/** Shared number, silver and time formatting. Matches the desktop app's reading. */

const decimal = new Intl.NumberFormat('en-US');

/** Silver reads in the game's own scale: 1.24b, 318m, 47.5k. */
export function formatSilver(value: number): string {
  const sign = value < 0 ? '-' : '';
  const amount = Math.abs(Math.round(value));
  if (amount >= 1_000_000_000) return `${sign}${trim(amount / 1_000_000_000)}b`;
  if (amount >= 1_000_000) return `${sign}${trim(amount / 1_000_000)}m`;
  if (amount >= 1_000) return `${sign}${trim(amount / 1_000)}k`;
  return `${sign}${amount}`;
}

function trim(value: number) {
  return value >= 100 ? Math.round(value).toString() : value.toFixed(value >= 10 ? 1 : 2).replace(/\.0+$/, '');
}

export function formatNumber(value: number): string {
  return decimal.format(value);
}

/** 1h 42m for durations over an hour, 42m 10s below. */
export function formatDuration(ms: number): string {
  const total = Math.max(0, Math.round(ms / 1000));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  if (hours) return `${hours}h ${String(minutes).padStart(2, '0')}m`;
  if (minutes) return `${minutes}m ${String(seconds).padStart(2, '0')}s`;
  return `${seconds}s`;
}

const relative = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });
const units: [Intl.RelativeTimeFormatUnit, number][] = [
  ['year', 31_536_000_000],
  ['month', 2_592_000_000],
  ['week', 604_800_000],
  ['day', 86_400_000],
  ['hour', 3_600_000],
  ['minute', 60_000],
];

export function formatRelative(iso: string): string {
  const elapsed = new Date(iso).getTime() - Date.now();
  if (Number.isNaN(elapsed)) return 'unknown';
  for (const [unit, duration] of units) {
    if (Math.abs(elapsed) >= duration) return relative.format(Math.round(elapsed / duration), unit);
  }
  return 'just now';
}

export function formatDateTime(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? 'unknown' : date.toLocaleString();
}

/** Two-letter monogram for a class or character name. */
export function monogram(value: string): string {
  const letters = value.replace(/[^\p{L}\p{N}]/gu, '');
  return letters.slice(0, 2).toUpperCase() || '??';
}
